// src/components/Navigation/MainNavBar.tsx
import React from 'react';
// Direct imports from MUI for better tree-shaking and test performance
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import { Link, useLocation } from 'react-router-dom';
import { CONTRACTS_AND_ASSETS, INVOICES, SUMMARY, HOME } from '../../../constants/messages.tsx';
import LesseeDropdown from '../../LesseeDropdown/LesseeDropdown';
import InternalLesseeDropdown from '../../LesseeDropdown/InternalLesseeDropdown.tsx';
import useMediaQuery from '@mui/material/useMediaQuery';
import { ROUTE_CONTRACTS, ROUTE_INVOICES, ROUTE_ROOT } from '../../../router/router.tsx';
import { useIsUserManagement } from '../../../hooks/useIsUserManagement';
import { useIsUserInternal } from '../../../hooks/useUserType.ts';
import { useUserHasRoleName } from '../../../hooks/useUserType.ts';
import { useUserAccountRole } from '../../../hooks/useUserAccountRole';

const MainNavBar: React.FC = () => {
	const isUserManagement = useIsUserManagement();
	const isInternalSalesRep = useUserHasRoleName('Int Sales Representative');
	const isInternalUser = useIsUserInternal();
	const { role } = useUserAccountRole();

	function normalizePath(path: string) {
		// Always ensure a leading slash and no trailing slash (except for root)
		if (!path.startsWith('/')) path = '/' + path;
		if (path.length > 1 && path.endsWith('/')) path = path.slice(0, -1);
		return path;
	}

	const navLinks = isUserManagement
		? [{ label: HOME, path: ROUTE_ROOT }]
		: [
				{ label: SUMMARY, path: ROUTE_ROOT },
				{ label: CONTRACTS_AND_ASSETS, path: ROUTE_CONTRACTS },
				...(role?.restrictInvoices ? [] : [{ label: INVOICES, path: ROUTE_INVOICES }]),
			];

	const isTabletOrMobile = useMediaQuery((theme: any) => theme.breakpoints.down('md'));
	const location = useLocation();

	return (
		<AppBar
			position="static"
			elevation={2}
			sx={{
				backgroundColor: (theme) => theme.palette.primary.main,
				height: '97px',
				width: '100%',
				justifyContent: 'center',
			}}
		>
			<Toolbar
				sx={{
					display: 'flex',
					justifyContent: { xs: 'flex-end', md: 'space-between' },
					alignItems: 'center',
					maxWidth: '1600px',
					width: { xs: '100%', sm: '80%', md: '90%' },
					p: '0',
					m: '0 auto',
				}}
			>
				{!isTabletOrMobile && (
					<Box sx={{ whiteSpace: 'nowrap' }}>
						{navLinks.map((link) => {
							const isActive = normalizePath(location.pathname) === normalizePath(link.path);
							return (
								<Button
									key={link.label}
									color="inherit"
									component={Link}
									to={link.path}
									sx={(theme) => ({
										marginRight: 2,
										fontSize: '1.125rem',
										textDecoration: isActive ? 'underline' : 'none',
										textUnderlineOffset: isActive ? '6px' : undefined,
										textDecorationColor: isActive ? theme.palette.secondary.main : undefined,
									})}
									data-testid={`nav-link-${link.label.replace(/\s+/g, '-').toLowerCase()}`}
								>
									{link.label}
								</Button>
							);
						})}
					</Box>
				)}

				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'flex-end',
						borderRadius: 1,
						minWidth: '300px',
						maxWidth: '500px',
						width: '100%',
					}}
				>
					{!isUserManagement &&
						((isInternalUser && !isInternalSalesRep) ? <InternalLesseeDropdown /> : <LesseeDropdown />)}
				</Box>
			</Toolbar>
		</AppBar>
	);
};

export default MainNavBar;
